// Public — approved reviews block for the product page. Renders an
// aggregate rating strip + the last N approved reviews. Returns null
// silently when the product has no approved review, so the layout
// stays tight while the boutique fills up with content.
import { useEffect, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";

const ProductReviewsBlock = ({ productId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    (async () => {
      const { ok, data: d } = await apiFetch(`/api/shop/products/${productId}/reviews`);
      if (cancelled) return;
      setData(ok ? d : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [productId]);

  if (loading || !data || !data.total) return null;

  const { items, total, average_rating, distribution } = data;
  const rounded = Math.round((average_rating || 0) * 10) / 10;

  return (
    <section
      className="py-14 md:py-16 border-t border-[color:var(--gv-line)] bg-white"
      data-testid="product-reviews"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-2 mb-6">
          <Star size={18} className="text-[color:var(--gv-pink)] fill-[color:var(--gv-pink)]" />
          <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">
            Avis clients ({total})
          </h2>
        </div>

        {/* Aggregate strip — average + histogram */}
        <div className="grid md:grid-cols-3 gap-6 mb-8 pb-8 border-b border-[color:var(--gv-line)]">
          <div className="md:col-span-1 text-center md:text-left">
            <div className="font-display text-5xl text-[color:var(--gv-ink)] leading-none mb-2" data-testid="reviews-average">
              {rounded.toFixed(1)}
            </div>
            <div className="flex items-center gap-0.5 justify-center md:justify-start mb-2">
              {[1,2,3,4,5].map((n) => (
                <Star
                  key={n}
                  size={17}
                  className={n <= Math.round(average_rating) ? "fill-[color:var(--gv-pink)] text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/20"}
                />
              ))}
            </div>
            <div className="text-[12px] text-[color:var(--gv-ink)]/55">{total} avis vérifiés</div>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            {[5,4,3,2,1].map((n) => {
              const count = distribution?.[String(n)] || 0;
              const pct = total ? (count / total) * 100 : 0;
              return (
                <div key={n} className="flex items-center gap-3 text-[12.5px]">
                  <span className="w-4 text-[color:var(--gv-ink)]/60">{n}</span>
                  <Star size={12} className="text-[color:var(--gv-pink)]/60 fill-[color:var(--gv-pink)]/60" />
                  <div className="flex-1 h-2 bg-[color:var(--gv-mist)] rounded-full overflow-hidden">
                    <div className="h-full bg-[color:var(--gv-pink)]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-right text-[color:var(--gv-ink)]/60 tabular-nums">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reviews list */}
        <div className="space-y-5">
          {items.slice(0, 10).map((r, idx) => (
            <div key={idx} className="border-b border-[color:var(--gv-line)] last:border-b-0 pb-5 last:pb-0" data-testid={`review-item-${idx}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-[14px] text-[color:var(--gv-ink)]">{r.display_name || "Client geoVINUM"}</div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} size={12} className={n <= (r.rating || 0) ? "fill-[color:var(--gv-pink)] text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/20"} />
                    ))}
                  </div>
                </div>
                {r.moderated_at && (
                  <div className="text-[11px] text-[color:var(--gv-ink)]/45">
                    {new Date(r.moderated_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                )}
              </div>
              {r.title && <div className="font-medium text-[14px] text-[color:var(--gv-ink)] mb-1">{r.title}</div>}
              {r.comment && <div className="text-[13.5px] text-[color:var(--gv-ink)]/80 leading-relaxed whitespace-pre-line">{r.comment}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductReviewsBlock;
